import type { ChannelRef, PersistedState, UiPrefs } from '../types'

const STORAGE_KEY = 'dggviewer:state'
const STATE_VERSION = 1

/**
 * Built-in channels: always present, always first, always favorited, and not
 * removable. Adding another is one line here -- nothing else in the app names a
 * channel, so ordering, fallbacks and the tab strip all derive from this list.
 *
 * `order` only decides display order among the built-ins, so it needs to stay
 * distinct; it becomes the sort key in `orderChannels`.
 */
function builtIn(slug: string, order: number): ChannelRef {
  return {
    slug,
    addedAt: new Date(order * 1000).toISOString(),
    favorite: true,
    builtIn: true,
  }
}

/**
 * Seed channels are installed once, only when there is no saved state. They
 * start unfavorited and are ordinary tabs, so they can be favorited, reordered
 * by the normal rules, or removed permanently.
 */
function seed(slug: string, order: number): ChannelRef {
  return {
    slug,
    addedAt: new Date(order * 1000).toISOString(),
    favorite: false,
    builtIn: false,
  }
}

export const DEFAULT_CHANNELS: ChannelRef[] = [builtIn('destiny', 0), builtIn('anythingelse', 1)]

const SEED_CHANNELS: ChannelRef[] = [...DEFAULT_CHANNELS, 
  seed('prsek', 2), 
  seed('dariusirl', 3), 
  seed('drt0', 4),
  seed('emiok', 5)
]

/** Landing channel when no tab is in the URL. */
export const DEFAULT_SLUG = DEFAULT_CHANNELS[0].slug

const DEFAULT_UI: UiPrefs = {
  chatSide: 'right',
  chatWidth: 390,
  delaySec: 2,
  lineLimit: 500,
  fontSize: 'medium',
  timeFormat: 'none',
  favoritesOnly: false,
  headerCollapsed: false,
  showSubFlairs: false,
  videoQuality: 0,
}

function safeParse(raw: string | null): PersistedState | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedState>
    if (!parsed || parsed.version !== STATE_VERSION) return null
    if (!Array.isArray(parsed.channels)) return null
    // Pick only known keys so removed prefs (e.g. `hideFlairs`) do not linger
    // in localStorage forever.
    const stored = (parsed.ui ?? {}) as Record<string, unknown>
    const ui = { ...DEFAULT_UI }
    for (const key of Object.keys(ui) as Array<keyof typeof ui>) {
      if (stored[key] !== undefined) {
        ;(ui as Record<string, unknown>)[key] = stored[key]
      }
    }
    if (!Number.isInteger(ui.videoQuality) || ui.videoQuality < 0) ui.videoQuality = 0
    return {
      version: STATE_VERSION,
      channels: parsed.channels.filter((c) => typeof c?.slug === 'string' && c.slug),
      ui,
    }
  } catch {
    return null
  }
}

export function loadState(): PersistedState {
  const loaded = safeParse(localStorage.getItem(STORAGE_KEY))
  if (!loaded) {
    return {
      version: STATE_VERSION,
      channels: SEED_CHANNELS,
      ui: { ...DEFAULT_UI },
    }
  }
  // Built-ins lead the strip in declared order. Anything already stored under
  // a built-in slug is dropped in favour of the canonical entry, so a user who
  // had added one manually ends up with the built-in version. Seed channels are
  // not merged here: once removed, they must stay removed.
  const rest = loaded.channels.filter((c) => !DEFAULT_CHANNELS.some((d) => d.slug === c.slug))
  return {
    version: STATE_VERSION,
    channels: [...DEFAULT_CHANNELS, ...rest],
    ui: { ...DEFAULT_UI, ...loaded.ui },
  }
}

export function saveState(state: PersistedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Quota or private-mode failure: the app still works, it just won't persist.
  }
}

/** Favorites first (in the order they were favorited), then the rest. */
export function orderChannels(channels: ChannelRef[]): ChannelRef[] {
  return [...channels].sort((a, b) => {
    if (a.favorite !== b.favorite) return a.favorite ? -1 : 1
    return a.addedAt.localeCompare(b.addedAt)
  })
}

export function addChannel(channels: ChannelRef[], slug: string): ChannelRef[] {
  if (channels.some((c) => c.slug === slug)) return channels
  return [...channels, { slug, addedAt: new Date().toISOString(), favorite: false, builtIn: false }]
}

export function removeChannel(channels: ChannelRef[], slug: string): ChannelRef[] {
  return channels.filter((c) => c.slug !== slug || c.builtIn)
}

export function toggleFavorite(channels: ChannelRef[], slug: string): ChannelRef[] {
  return channels.map((c) => (c.slug === slug && !c.builtIn ? { ...c, favorite: !c.favorite } : c))
}
